import { SEASON_CHALLENGE_DEFINITIONS } from '@brick-rogue/game-content';
import type {
  GameplayAnalyticsEvent,
  SeasonChallengeId,
  SeasonChallengeMetric,
} from '@brick-rogue/game-core';
import { recordSeasonChallengeProgress, type GameSave } from '@brick-rogue/persistence';

import { getActiveSeasonRotation } from './season';

const BOSS_WAVES = new Set([10, 20, 30]);

export interface SeasonGameplayProgressResult {
  readonly save: GameSave;
  readonly completedSeasonChallengeIds: readonly SeasonChallengeId[];
}

export function applySeasonGameplayEvent(
  save: GameSave,
  event: GameplayAnalyticsEvent,
  now: Date = new Date(),
): SeasonGameplayProgressResult {
  const rotation = getActiveSeasonRotation(now);
  if (!rotation) {
    return { save, completedSeasonChallengeIds: [] };
  }
  const definition = SEASON_CHALLENGE_DEFINITIONS[rotation.challengeId];
  const amount = getSeasonMetricIncrement(definition.metric, event);
  if (amount <= 0) {
    return { save, completedSeasonChallengeIds: [] };
  }
  const result = recordSeasonChallengeProgress(
    save,
    {
      seasonId: rotation.seasonId,
      seasonVersion: rotation.seasonVersion,
      rotationIndex: rotation.rotationIndex,
      challengeId: rotation.challengeId,
      amount,
      target: definition.target,
    },
    now.toISOString(),
  );
  return {
    save: result.save,
    completedSeasonChallengeIds:
      result.completedSeasonRecordIds.length > 0 ? [rotation.challengeId] : [],
  };
}

function getSeasonMetricIncrement(
  metric: SeasonChallengeMetric,
  event: GameplayAnalyticsEvent,
): number {
  switch (metric) {
    case 'ability-uses':
      return Number(event.type === 'ability-used');
    case 'bosses-defeated':
      return Number(event.type === 'wave-completed' && BOSS_WAVES.has(event.wave));
    case 'events-resolved':
      return Number(event.type === 'run-event-action');
    case 'completed-fusion-runs':
      return Number(
        event.type === 'run-ended' &&
          (event.reason === 'won' || event.reason === 'lost') &&
          event.ballFusionIds.length > 0,
      );
  }
}
